<!--
  StoreAllowancePolicy.vue -- Client Portal v2 PR B.

  Build contract:
  docs/planning/2026-06-30-client-portal-v2-data-api-refactor.md sections 5.1 /
  4.3. The v2 successor to the legacy StoreShopperAllowance card. Where the
  legacy card wrote a flat per-store ``company_allowance`` column, this card
  configures the new per-store ``allowance_policies`` rule:

    - amount: credit per shopper per period
    - cadence: monthly / quarterly / annual / one_time

  Allowance v1->v2 migration Slice 3 correction (Jake, 2026-07-09): this card
  does NOT expose an overflow / hybrid toggle. Cap-vs-hybrid is DERIVED from
  the store's payment method (Credit Card = hybrid, Invoice/Payroll = capped),
  enforced server-side via ``is_cc_store`` in ``resolve_order_funding``. There
  is no user-facing ``allow_overflow`` control; the card shows amount + cadence
  only and does not send ``allow_overflow`` in its PUT. See
  docs/planning/2026-07-09-payment-model-and-legacy-allowance-retirement.md
  (Part 1 correction + Slice 3 corrected scope).

  Self-contained persistence: own GET + PUT against
  /api/stores/<id>/allowance-policy/ (not part of campaign autosave).

  Card #596 (Jake, 2026-10-29, tightening 2026-10-28 option C): an allowance
  can only be offered on an EMAIL-DOMAIN store, where each shopper verifies
  their address and the allowance belongs to it (a shared-password store no
  longer qualifies). On any other store the card says so and will not turn an
  allowance on; one the store already has can still be turned off. The rule
  is the editor's mirror of the server table (utils/storeAccessRules.js),
  judged on the access mode the editor holds; the server refuses the PUT
  regardless, and its reason is what the owner sees.

  Card #596 part D (Jake, 2026-10-28): an optional store-wide cap on what ALL
  shoppers together may draw per period (the same period as the allowance).
  A shopper's remaining allowance is the lower of their own and what the cap
  has left. Blank = no cap (null). The server refuses a negative cap and
  enforces the cap on every draw; this card shows the store's usage this
  period (used vs cap), as the server derives it from the shoppers' grants.
-->
<template>
  <EditorCard
    title="Shopper Allowance"
    description="Optional per-shopper spending credit. Whether a shopper can spend beyond the allowance (hybrid) is set by the store's payment method, not here."
  >
    <div class="store-allowance" data-testid="store-allowance-policy">
      <div v-if="loading" data-testid="store-allowance-loading">Loading...</div>

      <template v-else>
        <v-alert
          v-if="needsRestriction"
          type="info"
          variant="tonal"
          density="compact"
          class="store-allowance-needs-restriction"
          data-testid="store-allowance-needs-restriction"
        >{{ restrictionNotice }}</v-alert>

        <v-switch
          class="text-left store-allowance-switch"
          :model-value="enabled"
          :disabled="needsRestriction && !enabled"
          label="Offer per-shopper allowance"
          hide-details
          data-testid="store-allowance-switch"
          @update:modelValue="onToggle"
        />

        <div v-if="enabled" class="store-allowance-drawer">
          <v-text-field
            prefix="$"
            inputmode="decimal"
            :model-value="amount"
            label="Allowance per shopper"
            density="comfortable"
            variant="outlined"
            data-testid="store-allowance-amount"
            @update:modelValue="amount = $event"
          />

          <v-select
            :model-value="cadence"
            :items="cadenceOptions"
            label="Reset cadence"
            density="comfortable"
            variant="outlined"
            data-testid="store-allowance-cadence"
            @update:modelValue="cadence = $event"
          />

          <v-text-field
            prefix="$"
            inputmode="decimal"
            :model-value="storeCap"
            label="Store-wide cap per period (optional)"
            hint="The most all shoppers together can use each period. Leave blank for no cap."
            persistent-hint
            density="comfortable"
            variant="outlined"
            data-testid="store-allowance-store-cap"
            @update:modelValue="storeCap = $event"
          />

          <p
            v-if="usageText"
            class="store-allowance-cap-usage text-medium-emphasis"
            data-testid="store-allowance-cap-usage"
          >
            {{ usageText }}
          </p>

          <!--
            When the allowance renews (Bug Intake #483, Jake 2026-10-23:
            calendar-based reset). The sentence comes from the server, derived
            from the same period math checkout uses, so it cannot disagree
            with when a shopper's balance really resets.
          -->
          <p
            v-if="resetRule"
            class="store-allowance-reset-rule text-medium-emphasis"
            data-testid="store-allowance-reset-rule"
          >
            {{ resetRule }}
          </p>

          <div class="store-allowance-actions">
            <v-btn
              color="primary"
              rounded="pill"
              :loading="saving"
              :disabled="needsRestriction"
              data-testid="store-allowance-save"
              @click="save"
            >
              Save allowance policy
            </v-btn>
          </div>
        </div>
      </template>
    </div>
  </EditorCard>
</template>

<script>
import axios from "@/axios";
import EditorCard from "./components/EditorCard.vue";
import { parseCurrencyInput } from "@/utils/currency";
import {
  allowancePermitted,
  allowanceRestrictionNotice,
} from "@/utils/storeAccessRules";

const SAVE_FAILED = "Failed to save allowance policy";
const BAD_CAP =
  "The store-wide cap must be an amount of $0 or more, or blank for no cap.";

function dollars(n) {
  return `$${Number(n).toFixed(2)}`;
}

// The server's own reason for a refused save (a 4xx JSON ``message``, e.g.
// the #596 restriction refusal); the generic text otherwise.
function refusalMessage(err) {
  const status = err && err.response && err.response.status;
  const message = err && err.response && err.response.data
    ? err.response.data.message
    : null;
  if (status >= 400 && status < 500 && typeof message === "string" && message) {
    return message;
  }
  return SAVE_FAILED;
}

export default {
  name: "StoreAllowancePolicy",

  components: { EditorCard },

  props: {
    storeId: { type: [Number, String], required: true },
    // The store's "Who can shop here" mode as the editor holds it. Unset is
    // judged like an open store (fail closed).
    accessMode: { type: String, default: null },
  },

  data() {
    return {
      loading: true,
      saving: false,
      enabled: false,
      // True once the SERVER holds a policy row for this store (active or
      // not). Turning the switch off/on only needs a PUT when it does;
      // otherwise there is nothing saved to deactivate or re-activate.
      hasPolicy: false,
      // The amount/cadence the SERVER holds. The on/off toggle sends these,
      // never the (possibly unsaved or blank) drawer fields: flipping the
      // switch must not silently save an edit. Save sends the typed values.
      serverAmount: 0,
      serverCadence: "monthly",
      // The store-wide cap the SERVER holds (null = no cap) and the drawer's
      // typed value (#596 part D). The switch re-sends the saved one.
      serverStoreCap: null,
      storeCap: "",
      // { used, cap, remaining, period_start, period_end } from the GET.
      capUsage: null,
      amount: "",
      cadence: "monthly",
      cadenceOptions: ["monthly", "quarterly", "annual", "one_time"],
      // { cadence: "Resets on ..." } as the server states it (#483).
      resetRules: {},
    };
  },

  computed: {
    resetRule() {
      return this.resetRules[this.cadence] || "";
    },
    // Card #596: not an email-domain store, so no allowance may be
    // turned on here.
    needsRestriction() {
      return !allowancePermitted(this.accessMode);
    },
    restrictionNotice() {
      return allowanceRestrictionNotice();
    },
    // The owner's "used vs cap" for this period (#596 part D).
    usageText() {
      const u = this.capUsage;
      if (!u || u.used === null || u.used === undefined) {
        return "";
      }
      if (u.cap === null || u.cap === undefined) {
        return `${dollars(u.used)} used by shoppers this period. No store-wide cap.`;
      }
      return (
        `${dollars(u.used)} of the ${dollars(u.cap)} store-wide cap used this ` +
        `period (${dollars(u.remaining)} left).`
      );
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    // The switch always shows what the SERVER holds. Every load and every
    // successful PUT goes through here (Bug Intake #353: the page used to keep
    // its own local on/off and drift from the saved state).
    applyServerPolicy(policy, { keepDraft = false } = {}) {
      this.hasPolicy = !!policy;
      if (policy) {
        const n = Number(policy.amount);
        this.serverAmount = Number.isFinite(n) && n > 0 ? n : 0;
        this.serverCadence = policy.cadence || "monthly";
        this.enabled = policy.is_active !== false;
        const capN = policy.store_cap_amount;
        this.serverStoreCap =
          capN === null || capN === undefined || !Number.isFinite(Number(capN))
            ? null
            : Number(capN);
        // A toggle must not overwrite what the user has typed in the drawer
        // and not yet saved: only the saved state is refreshed.
        if (!keepDraft) {
          this.amount = String(policy.amount ?? "");
          this.cadence = policy.cadence || "monthly";
          this.storeCap =
            this.serverStoreCap === null ? "" : String(this.serverStoreCap);
        }
      } else {
        this.enabled = false;
      }
    },

    async load() {
      this.loading = true;
      try {
        const resp = await axios.get(
          `/api/stores/${this.storeId}/allowance-policy/`,
        );
        this.applyServerPolicy(resp.data && resp.data.policy);
        this.resetRules = (resp.data && resp.data.reset_rules) || {};
        this.capUsage = (resp.data && resp.data.store_cap_usage) || null;
      } catch (e) {
        this.enabled = false;
      } finally {
        this.loading = false;
      }
    },

    currentAmount() {
      const parsed = parseCurrencyInput(this.amount);
      return parsed === null || parsed < 0 ? 0 : parsed;
    },

    // The typed cap: null when blank (no cap), undefined when it is not a
    // usable amount (negative or not a number) -- Save refuses that.
    currentStoreCap() {
      const raw = this.storeCap === null || this.storeCap === undefined
        ? ""
        : String(this.storeCap).trim();
      if (raw === "") {
        return null;
      }
      const parsed = parseCurrencyInput(raw);
      if (parsed === null || !Number.isFinite(parsed) || parsed < 0) {
        return undefined;
      }
      return parsed;
    },

    // Re-read the usage after a save (the cap it counts against may have
    // changed). Only the usage is taken: the drawer keeps what was saved.
    async refreshUsage() {
      try {
        const resp = await axios.get(
          `/api/stores/${this.storeId}/allowance-policy/`,
        );
        this.capUsage = (resp.data && resp.data.store_cap_usage) || null;
      } catch (e) {
        // The save itself succeeded; a stale usage line is not an error.
      }
    },

    async putPolicy(isActive, { fromServer = false } = {}) {
      const resp = await axios.put(
        `/api/stores/${this.storeId}/allowance-policy/`,
        {
          amount: fromServer ? this.serverAmount : this.currentAmount(),
          cadence: fromServer ? this.serverCadence : this.cadence,
          is_active: isActive,
          store_cap_amount: fromServer
            ? this.serverStoreCap
            : this.currentStoreCap(),
        },
      );
      if (resp.data) {
        this.applyServerPolicy(resp.data, { keepDraft: fromServer });
      }
      return resp.data;
    },

    raiseError(message) {
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch("raiseError", message);
      }
    },

    async onToggle(val) {
      const want = !!val;
      // Turning ON is refused off an email-domain store (#596); the
      // switch is disabled then, this covers any event that reaches us.
      if (want && this.needsRestriction) {
        this.enabled = false;
        return;
      }
      // Nothing saved yet, or turning ON a policy that was never configured
      // (amount 0): just open/close the drawer. Saving the amount activates
      // it; re-activating a $0 policy would cap every shopper at $0.
      if (!this.hasPolicy || (want && this.serverAmount <= 0)) {
        this.enabled = want;
        return;
      }
      this.saving = true;
      try {
        await this.putPolicy(want, { fromServer: true });
        this.$emit("saved");
      } catch (e) {
        // The save failed, so the server still holds the old state: keep the
        // switch there rather than showing a state that was never saved.
        this.enabled = !want;
        this.raiseError(refusalMessage(e));
      } finally {
        this.saving = false;
      }
    },

    async save() {
      if (this.needsRestriction) {
        return;
      }
      if (this.currentStoreCap() === undefined) {
        this.raiseError(BAD_CAP);
        return;
      }
      this.saving = true;
      try {
        await this.putPolicy(true);
        await this.refreshUsage();
        this.$emit("saved");
      } catch (e) {
        this.raiseError(refusalMessage(e));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Vuetify puts data-testid on the switch's ROOT, which would otherwise span
   the whole card: a click on its centre (a user's, or the ui-runner's) lands
   in empty space to the right of the switch and toggles nothing. */
.store-allowance-switch {
  display: inline-flex;
  flex: 0 0 auto;
}
.store-allowance-needs-restriction {
  margin-bottom: 8px;
}
.store-allowance-drawer {
  margin-top: 8px;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.store-allowance-cap-usage {
  margin: 0 0 4px;
  font-size: 0.875rem;
}
.store-allowance-reset-rule {
  margin: -4px 0 4px;
  font-size: 0.875rem;
}
.store-allowance-actions {
  margin-top: 4px;
}
</style>
